import {get, isEmpty, isNil} from 'lodash'
import {accountHasFeature, getProLicencesUsed, userHasPermission} from 'helpers/user'
import {features, permissions, PRO_LICENCE} from 'helpers/constants'
import {getSearchTerm, isStringMatching} from 'helpers/string'
import {getDaysDueLabel, getDaysToDue, roundDaysDown} from 'helpers/dates'
import {getNonDormantOrderItems} from 'helpers/orders'
import {DeliveryStatus, isDelivered} from 'helpers/deliveries'
import {areLineItemJobCardsEnabled} from 'helpers/production/setupJobCardDialog'
import {getContactName} from 'helpers/contacts'
import {JobCardDeliveredStatusOption, UnallocatedOption, updateParamsForOrderType} from 'helpers/opportunities'
import {UserTypes} from 'helpers/production/jobBoardListView'
import {statusesToExcludeFromProduction} from 'helpers/quotes'

export const DepartmentType = {
  PRODUCTION: 'PRODUCTION',
  DELIVERY: 'DELIVERY',
  DELIVERY_THIRD_PARTY: 'DELIVERY_THIRD_PARTY',
}

export const isProductionDepartmentType = (departmentType) => {
  return departmentType === DepartmentType.PRODUCTION
}

export const isDeliveryDepartmentType = (departmentType) => {
  return departmentType === DepartmentType.DELIVERY
}

export const WorkCentreStatus = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
}

export const isWorkCentreEnabled = (workCentreStatus) => {
  return workCentreStatus === WorkCentreStatus.ENABLED
}

export const ProductionReasonType = {
  onHold: 0,
}

export const ProductionStatus = {
  unscheduled: -1,
  scheduled: 0,
  wip: 1,
  onHold: 2,
  complete: 3,
}

export const JobBoardLanes = {
  pending: -2,
  completed: -3,
}

export const JobBoardStatus = {
  notScheduled: -1,
  pending: 0,
  wip: 1,
  onHold: 2,
  complete: 3,
  noProductionRequired: 4,
  delivered: 5,
}

export const getJobBoardStatusName = (status) => {
  switch (status) {
    case JobBoardStatus.pending:
      return 'Pending'
    case JobBoardStatus.wip:
      return 'WIP'
    case JobBoardStatus.onHold:
      return 'On Hold'
    case JobBoardStatus.complete:
      return 'Complete'
    case JobBoardStatus.notScheduled:
      return 'Not Scheduled'
    case JobBoardStatus.noProductionRequired:
      return 'No Production Required'
    default:
      return null
  }
}

export const getColorForJobCardStatus = (muiTheme, status) => {
  if (isNil(status)) return ''
  switch (status) {
    case JobBoardStatus.pending:
      return muiTheme.palette.jobCardStatus.pending
    case JobBoardStatus.wip:
      return muiTheme.palette.jobCardStatus.wip
    case JobBoardStatus.onHold:
      return muiTheme.palette.jobCardStatus.onHold
    case JobBoardStatus.complete:
      return muiTheme.palette.jobCardStatus.complete
    case JobBoardStatus.notScheduled:
      return muiTheme.palette.jobCardStatus.notScheduled
    case JobBoardStatus.noProductionRequired:
      return muiTheme.palette.jobCardStatus.noProductionRequired
    case JobBoardStatus.delivered:
      return muiTheme.palette.jobCardStatus.delivered
    default:
      return ''
  }
}

export const getJobBoardStatusOptions = (productionCompleteName) => [
  {value: JobBoardStatus.notScheduled, label: 'Not Scheduled'},
  {value: JobBoardStatus.pending, label: 'Pending'},
  {value: JobBoardStatus.wip, label: 'WIP'},
  {value: JobBoardStatus.onHold, label: 'On Hold'},
  {value: JobBoardStatus.complete, label: productionCompleteName},
  {value: JobBoardStatus.noProductionRequired, label: 'No Production Required'},
]

export const getJobCardStatusOptions = () => [
  {value: JobBoardStatus.pending, label: 'Pending'},
  {value: JobBoardStatus.wip, label: 'WIP'},
  {value: JobBoardStatus.onHold, label: 'On Hold'},
  {value: JobBoardStatus.complete, label: 'Complete'},
]

export const isProductionEnabled = (user) => (
  accountHasFeature(user, features.PRODUCTION_LICENCE) &&
  !user.settings.productionDisabled
)

export const getOrderOverdueWarning = (order, warningPeriod, includeTarget = true, taskName = 'Job') => {
  let requiredDate = get(order, 'requiredDate', null)
  let targetDate = get(order, 'targetDate', null)
  if (requiredDate) requiredDate = roundDaysDown(new Date(requiredDate)).getTime()
  if (targetDate) targetDate = includeTarget
    ? roundDaysDown(new Date(targetDate)).getTime()
    : null

  const todayDate = roundDaysDown(new Date())
  const today = todayDate.getTime()
  const warnDate = new Date(todayDate).setDate(todayDate.getDate() + warningPeriod - 1)

  if (isNil(requiredDate) && isNil(targetDate))
    return null

  if (!isNil(requiredDate) && today > requiredDate)
    return {label: `${taskName} is overdue its required date`, level: 'ERROR'}

  if (!isNil(targetDate) && today > targetDate)
    return {label: `${taskName} is overdue its target completion date`, level: 'ERROR'}

  if (!isNil(targetDate) && !isNil(requiredDate) && targetDate > requiredDate)
    return {label: `${taskName} is due to finish after its required date`, level: 'ERROR'}

  if (warningPeriod > 0 && !isNil(requiredDate) && warnDate >= requiredDate) {
    const daysDue = getDaysToDue(today, requiredDate)
    return {label: getDaysDueLabel(daysDue, 'required', taskName), level: 'WARNING'}
  }

  if (warningPeriod > 0 && !isNil(targetDate) && warnDate >= targetDate) {
    const daysDue = getDaysToDue(today, targetDate)
    return {label: getDaysDueLabel(daysDue, 'due', taskName), level: 'WARNING'}
  }

  return null
}

export const getJobCardStatusClass = (jobCard) => {
  if (isNil(jobCard)) return 'pending'

  switch (jobCard.departmentStatus) {
    case JobBoardStatus.pending:
      return 'pending'
    case JobBoardStatus.wip:
      return 'wip'
    case JobBoardStatus.onHold:
      return 'onHold'
    case JobBoardStatus.complete:
      return 'complete'
  }
  return 'pending'
}

export const updateJobCard = (jobCard, key, value) => {
  return {
    ...jobCard,
    [key]: value,
  }
}

export const isJobCardPending = (order, jobCard) => {
  const productionScheduled = get(order, 'productionScheduled')
  const jobStatus = get(order, 'jobStatus')

  return productionScheduled === false
    && jobStatus !== JobBoardStatus.noProductionRequired
    && isNil(jobCard)
}

export const isJobCardOnHold = (jobCard) => {
  return get(jobCard, 'departmentStatus', null) === JobBoardStatus.onHold
}

export const isJobCardComplete = (order, jobCard) =>
  !isNil(jobCard) &&
  jobCard.status === JobBoardStatus.complete &&
  isNil(jobCard.departmentId)

export const canStartOrderJobCard = (user, order, jobCard) =>
  userHasPermission(user, permissions.MANAGE_JOB_CARDS) &&
  isJobCardPending(order, jobCard)

export const canStartJobCard = (user, jobCard) => {
  return userHasPermission(user, permissions.MANAGE_JOB_CARDS) &&
    (jobCard.departmentStatus === JobBoardStatus.pending || jobCard.departmentStatus === JobBoardStatus.onHold)
}

export const canChangeDepartmentStatus = (jobCard) => {
  const departmentId = get(jobCard, 'departmentId', null)
  const departmentStatus = get(jobCard, 'departmentStatus', null)
  return !isNil(departmentId) && departmentStatus !== JobBoardStatus.complete
}

export const canCompleteJobCard = (user) =>
  userHasPermission(user, permissions.MANAGE_JOB_CARDS)

export const canMoveJobCardToAnotherDepartment = (user, order, jobCard) =>
  userHasPermission(user, permissions.MANAGE_JOB_CARDS) &&
  !isJobCardPending(order, jobCard)

export const getCompleteJobCards = (jobCards) => {
  return jobCards.filter(jobCard => isJobCardComplete(jobCard.order, jobCard))
}

export const getJobCardsForDepartment = (departmentId, jobCards) => {
  return isEmpty(jobCards)
    ? []
    : jobCards.filter(jobCard => jobCard.departmentId === departmentId)
}

export const filterDepartments = (departments, filteredDepartments) => {
  if (isEmpty(filteredDepartments)) return departments
  return departments.filter(department => filteredDepartments.includes(department.id))
}

export const mapJobCardsWithReasonName = (jobCards, onHoldReasons) => {
  return jobCards.map(jobCard => {
    const onHoldReason = onHoldReasons.find(onHoldReason => onHoldReason.id === jobCard.onHoldReasonId)
    return {
      ...jobCard,
      onHoldReasonName: isNil(onHoldReason)
        ? null
        : onHoldReason.name,
    }
  })
}

export const filterJobCards = (
  jobCards,
  filteredStatuses,
  filteredOwners,
  filteredTargetStart,
  filteredTargetEnd,
  searchWithPartCode,
  selectedOrderTypeIds
) => {
  const ret = jobCards
    .filter(jobCard =>
      isEmpty(filteredOwners)
      || jobCard.jobCardOwners.some(owner => filteredOwners.includes(owner.contactId)))
    .filter(jobCard =>
      isEmpty(filteredStatuses)
      || filteredStatuses.includes(jobCard.departmentStatus))
    .filter(jobCard =>
      isOrderIncludedInFilters(jobCard.order, filteredTargetStart, filteredTargetEnd))
    .filter(jobCard =>
      isEmpty(selectedOrderTypeIds)
      || (selectedOrderTypeIds.includes(UnallocatedOption.value) && isNil(jobCard.order.typeId))
      || selectedOrderTypeIds.includes(jobCard.order.typeId)
    )

  return filterJobCardsWithSearch(ret, searchWithPartCode)
}

export const filterJobCardsWithSearch = (jobCards, searchWithPartCode) => {
  return !searchWithPartCode
    ? jobCards
    : jobCards.filter(jobCard => {
      return (
        isStringMatching(get(jobCard, 'order.ref'), searchWithPartCode) ||
        isStringMatching(get(jobCard, 'order.desc'), searchWithPartCode) ||
        isStringMatching(get(jobCard, 'order.company.name'), searchWithPartCode) ||
        isStringMatching(get(jobCard, 'onHoldReasonName'), searchWithPartCode) ||
        isStringMatching(get(jobCard, 'onHoldNotes'), searchWithPartCode) ||
        (jobCard.orderItem
          ? isStringMatching(jobCard.orderItem.partCode, searchWithPartCode) ||
          (
            !isEmpty(jobCard.orderItem.allSubItems) &&
            jobCard.orderItem.allSubItems.some(subItem =>
              isStringMatching(subItem.partCode, searchWithPartCode))
          )
          : jobCard.order.allItems.some(item => isStringMatching(item.partCode, searchWithPartCode))) ||
        jobCard.jobCardOwners.some(owner =>
          isStringMatching(getContactName(owner.contact), searchWithPartCode))
      )
    })
}

export const filterOrders = (orders, filteredStatuses) => {
  return isPendingStatusEnabled(filteredStatuses)
    ? orders
    : []
}

export const isOrderIncludedInFilters = (order, filteredTargetStart, filteredTargetEnd) => {
  if (isNil(filteredTargetStart) ||
    isNil(filteredTargetEnd)) {
    return true
  }

  return !isNil(order) &&
    !isNil(order.targetDate) &&
    order.targetDate >= filteredTargetStart &&
    order.targetDate < filteredTargetEnd
}

export const getOrderCount = (orderCount, filteredStatuses) => {
  return isPendingStatusEnabled(filteredStatuses)
    ? orderCount
    : 0
}

export const isPendingStatusEnabled = (filteredStatuses) => {
  return isEmpty(filteredStatuses) ||
    filteredStatuses.includes(JobBoardStatus.pending)
}

export const isPendingLaneEnabled = (filteredDepartments) => {
  return isEmpty(filteredDepartments) ||
    filteredDepartments.includes(JobBoardLanes.pending)
}

export const isCompletedLaneEnabled = (filteredDepartments) => {
  return isEmpty(filteredDepartments) ||
    filteredDepartments.includes(JobBoardLanes.completed)
}

export const isFiltering = (statuses, departments, targetStart, targetEnd) =>
  !isEmpty(statuses) ||
  !isEmpty(departments) ||
  (!isNil(targetStart) && !isNil(targetEnd))

export const getOnHoldReasons = (productionReasons) =>
  productionReasons.filter(reason => reason.type === ProductionReasonType.onHold)

export const showOnHoldReason = jobCard => !isNil(jobCard) && jobCard.departmentStatus === JobBoardStatus.onHold

export const getHoldReason = (jobCard, onHoldReasons) =>
  onHoldReasons.find(reason => reason.id === jobCard.onHoldReasonId)

export const isOrderProductionComplete = (order) => {
  return order.jobStatus === JobBoardStatus.complete
}

export const isOrderProductionPending = (order) => {
  return order.jobStatus === JobBoardStatus.pending
}

export const isWorkCentreExpanded = (expandedWorkCentres, departmentId, workCentreId) => {
  if (isEmpty(expandedWorkCentres)) return true
  return get(expandedWorkCentres.find(workCentre =>
    workCentre.workCentreId === workCentreId &&
    workCentre.departmentId === departmentId
  ), 'expanded', true)
}

export const JobCardCreateType = {
  JOB: 'JOB',
  LINE_ITEM: 'LINE_ITEM',
}

export const isLineItemCreateType = (jobCreateType) => jobCreateType === JobCardCreateType.LINE_ITEM

export const getJobCardCreateTypeOptions = () => [
  {
    id: JobCardCreateType.JOB,
    key: JobCardCreateType.JOB,
    value: JobCardCreateType.JOB,
    label: 'For the Whole Job',
  },
  {
    id: JobCardCreateType.LINE_ITEM,
    key: JobCardCreateType.LINE_ITEM,
    value: JobCardCreateType.LINE_ITEM,
    label: 'For Each Line Item',
  },
]

export const getJobCardCreateType = (order) => {
  let jobCards = get(order, 'jobCards', [])
  jobCards = isEmpty(jobCards) ? [] : jobCards
  return jobCards.length === 1 && isNil(jobCards[0].orderItemId)
    ? JobCardCreateType.JOB
    : JobCardCreateType.LINE_ITEM
}

export const getDepartmentValue = (departmentId, departmentOption) => {
  if (typeof departmentOption.departmentId === 'boolean') {
    return departmentOption.departmentId === true
      ? departmentId
      : null
  }

  return departmentOption.departmentId
}

export const getSelectedDepartments = (orderItem) => {
  return Object.entries(orderItem)
    .filter(item => item[0].startsWith('department_'))
    .map(item => {
      const departmentId = parseInt(item[0].replace('department_', ''))
      const option = item[1]
      return {
        departmentId: getDepartmentValue(departmentId, option),
        workCentreId: option.workCentreId,
      }
    })
}

export const prepareJobCardLineItems = (orderItems, departments, workCentres) => {
  return orderItems.map(orderItem => {
    const updatedOrderItem = {...orderItem}
    updatedOrderItem.selected = isNil(orderItem.jobCard)
    updatedOrderItem.jobCardNotes = get(orderItem, 'notes', '')
    departments.forEach(department => {
      const departmentWorkCentres = workCentres.filter(workCentre => workCentre.departmentId === department.id)
      const departmentKey = `department_${department.id}`
      updatedOrderItem[departmentKey] = {
        departmentId: true,
        workCentreId: isEmpty(departmentWorkCentres)
          ? null
          : departmentWorkCentres[0].id,
      }
    })
    return updatedOrderItem
  })
}

export const getJobCardLineItems = (orderItems, sortOrder) => {
  return orderItems.map(orderItem => {
    const owners = get(orderItem, 'owners', [])
    const updatedOrderItem = {
      orderId: orderItem.orderId,
      orderItemId: orderItem.id,
      selectedDepartments: getSelectedDepartments(orderItem),
      selectedOwners: owners.map(owner => {
        return {
          jobCardId: -1,
          contactId: owner,
        }
      }),
      description: orderItem.desc,
      jobCardNotes: orderItem.jobCardNotes,
      sortOrder,
    }
    sortOrder++
    return updatedOrderItem
  })
}

export const ProductionItemType = {
  DEFAULT_COLUMN: 'DEFAULT_COLUMN',
  DEPARTMENT: 'DEPARTMENT',
  WORK_CENTRE: 'WORK_CENTRE',
}

export const updateWorkCentreSortOrder = (workCentre, departments) => {
  const department = findDepartmentByGuid(departments, workCentre.department.guid)

  return {
    ...workCentre,
    department: {
      ...workCentre.department,
      sortOrder: department.sortOrder,
    },
  }
}

export const findDepartmentByGuid = (departments, guid) => {
  return departments.find(department => department.guid === guid)
}

export const getUnscheduledJobCardLineItems = (order) => {
  const jobCards = get(order, 'jobCards', [])
  const orderItems = getNonDormantOrderItems(get(order, 'orderitems', []))

  return {
    jobCards: isEmpty(jobCards) ? [] : jobCards,
    orderItems: isEmpty(orderItems) ? [] : orderItems,
  }
}

export const isAnyOrderItemUnscheduled = (order) => {
  const {jobCards, orderItems} = getUnscheduledJobCardLineItems(order)
  return jobCards.length < orderItems.length
}

export const hasDefaultWorkCentreChangedForCurrentDepartment = (jobCard, updatedJobCard) => {
  if (isNil(jobCard.departmentId) || isNil(jobCard.workCentreId)) return false

  const existingJobCardDept = jobCard.jobCardDepartments
    .find(jobCardDepartment => jobCardDepartment.departmentId === jobCard.departmentId)

  const updatedJobCardDept = updatedJobCard.jobCardDepartments
    .find(jobCardDepartment => jobCardDepartment.departmentId === jobCard.departmentId)

  return (
    !isNil(existingJobCardDept) &&
    !isNil(updatedJobCardDept) &&
    existingJobCardDept.workCentreId !== updatedJobCardDept.workCentreId &&
    updatedJobCardDept.workCentreId !== jobCard.workCentreId
  )
}

export const doesJobHaveProduction = (order) =>
  !isNil(order.jobStatus) && order.jobStatus !== JobBoardStatus.noProductionRequired

export const getDeliveredQuantity = (jobCard, deliveryNotes) => {
  return deliveryNotes
    .filter(deliveryNote => !deliveryNote.returnNote)
    .map(deliveryNote => {
      const items = get(deliveryNote, 'deliveryNoteItems', []) || []
      return items.find(item => item.orderItemId === jobCard.orderItemId)
    })
    .filter(item => !isNil(item))
    .reduce((total, item) => total + item.quantity, 0)
}

export const getReturnItemsQuantity = (jobCard, returnNotes) => {
  return isEmpty(returnNotes)
    ? 0
    : returnNotes.map(item => {
      const items = get(item, 'deliveryNoteItems', []) || []
      return items.find(item => item.orderItemId === jobCard.orderItemId)
    })
      .filter(item => !isNil(item))
      .reduce((total, item) => total + item.quantity, 0)
}

export const getJobCardDeliveryStatus = (jobCard, order) => {
  if (isNil(jobCard) || isNil(jobCard.orderItemId))
    return get(order, 'deliveryStatus.id', null)

  const deliveryNotes = get(order, 'deliveryNotes', [])
    .filter(deliveryNote => isDelivered(deliveryNote.status))
  const returnNotes = deliveryNotes.filter(item => item.returnNote)

  const deliveredQuantity = getDeliveredQuantity(jobCard, deliveryNotes)
  const returnItemsQuantity = getReturnItemsQuantity(jobCard, returnNotes)
  const totalDeliveredQuantity = deliveredQuantity - returnItemsQuantity

  return totalDeliveredQuantity >= get(jobCard, 'orderItem.quantity', 0)
    ? DeliveryStatus.fullyDelivered
    : totalDeliveredQuantity > 0
      ? DeliveryStatus.partDelivered
      : DeliveryStatus.none
}

export const getOrderJobCards = (orderId, allJobCards) =>
  allJobCards.filter(jobCard => jobCard.orderId === orderId)

export const hasAnyLineItemJobCards = (jobCards) =>
  jobCards.some(jobCard => !isNil(jobCard.orderItemId))

export const canOnlyLoadLineItemJobCards = (user, orderId, allJobCards) =>
  areLineItemJobCardsEnabled(user) &&
  hasAnyLineItemJobCards(getOrderJobCards(orderId, allJobCards))

export const isProProductionEnabled = (user) => {
  const productionLicences = getProLicencesUsed(user, PRO_LICENCE.Production)
  return !isNil(productionLicences) && productionLicences > 0
}

export const getWorkCentreName = (workCentre) => {
  const workCentreName = get(workCentre, 'name', null)
  const departmentName = get(workCentre, 'department.name', null)
  return [departmentName, workCentreName]
    .filter(property => !isEmpty(property))
    .join('/')
}

export const updateParamsForJobBoardStatusTypes = (params, statusTypes) => {
  if (isEmpty(statusTypes))
    return

  const statusTypesOptions = statusTypes
    .filter(statusType => !isNil(statusType) && statusType !== JobCardDeliveredStatusOption.value)

  if (!isEmpty(statusTypesOptions))
    params.status = statusTypesOptions

  if (statusTypes.some(jobType => jobType === JobCardDeliveredStatusOption.value))
    params.showJobBoardDeliveredStatus = true
}

export const getParamsWithRoles = (params, selectedContacts, selectedRoles) => {
  if (!isEmpty(selectedContacts)) {
    params['personId'] = selectedContacts

    const allUserTypes = UserTypes.map(type => type.value)
    if (!isEmpty(selectedRoles)) {
      selectedRoles.forEach(type => {
        params[type] = true
      })
      const nonSelectedUserTypes = allUserTypes.filter(filter => !selectedRoles.includes(filter))
      nonSelectedUserTypes.forEach(type => {
        params[type] = false
      })
    } else {
      allUserTypes.forEach(type => {
        params[type] = true
      })
    }
  }
  return params
}

export const getJobBoardFilterParams = (
  search,
  statusTypes,
  orderTypeId,
  companies,
  dateTypes,
  targetStart,
  targetEnd,
  selectedDepartments,
  selectedWorkCentres,
  selectedRoles,
  selectedContacts
) => {

  const params = {}
  if (getSearchTerm(search))
    params['search'] = search

  if (companies)
    params['companyId'] = companies.map(company => company.value)

  const updatedParams = getParamsWithRoles(params, selectedContacts, selectedRoles)

  if (!isEmpty(selectedDepartments)) {
    updatedParams['departments'] = selectedDepartments
  }

  if (!isEmpty(selectedWorkCentres)) {
    updatedParams['workCentres'] = selectedWorkCentres
  }

  if (targetStart && targetEnd) {
    dateTypes.forEach(dateType => {
      updatedParams[`${dateType}Start`] = targetStart
      updatedParams[`${dateType}End`] = targetEnd
    })
  }

  updateParamsForJobBoardStatusTypes(updatedParams, statusTypes)
  updateParamsForOrderType(updatedParams, orderTypeId)

  return updatedParams
}

export const isJobCardDelivered = (jobCard, order) => {
  return getJobCardDeliveryStatus(jobCard, order) === DeliveryStatus.fullyDelivered
}

export const shouldExistInOrderBook = (order, search, targetStart, targetEnd) => {
  return (
    isOrderValidForOrderBook(order) &&
    isOrderFilterCompliant(order, search, targetStart, targetEnd)
  )
}

export const isOrderValidForOrderBook = (order) => {
  const isProductionScheduled = get(order, 'productionScheduled')
  const jobStatus = get(order, 'jobStatus')
  const status = get(order, 'status.value')
  const deliveryStatus = get(order, 'deliveryStatus.id')

  const isStatusValid = !statusesToExcludeFromProduction.includes(status)
  const isProductionRequired = jobStatus !== JobBoardStatus.noProductionRequired
  const isFullyDelivered = deliveryStatus === DeliveryStatus.fullyDelivered

  return (
    isStatusValid &&
    isProductionRequired &&
    !isProductionScheduled &&
    !isFullyDelivered
  )
}

export const isOrderFilterCompliant = (order, search, targetStart, targetEnd) => {
  const targetDate = get(order, 'targetDate')
  const ref = get(order, 'ref', '')
  const desc = get(order, 'desc', '')
  const orderNumber = get(order, 'orderNumber', '')
  const companyName = get(order, 'company.name', '')
  const contactName = get(order, 'contact.name', '')

  const isFilteringTargetDate = !isNil(targetStart) && !isNil(targetEnd)
  const targetDateIsValid = (
    !isNil(targetDate) &&
    targetDate >= targetStart &&
    targetDate <= targetEnd
  )

  if (isFilteringTargetDate && !targetDateIsValid)
    return false

  const isSearching = !isEmpty(search)
  if (!isSearching)
    return true

  const refIsValid = !isEmpty(ref) && ref.toString().toLowerCase().includes(search)
  const descIsValid = !isEmpty(desc) && desc.toString().toLowerCase().includes(search)
  const orderNumberIsValid = !isEmpty(orderNumber) && orderNumber.toString().toLowerCase().includes(search)
  const companyNameIsValid = !isEmpty(companyName) && companyName.toString().toLowerCase().includes(search)
  const contactNameIsValid = !isEmpty(contactName) && contactName.toString().toLowerCase().includes(search)

  return (
    refIsValid ||
    descIsValid ||
    orderNumberIsValid ||
    companyNameIsValid ||
    contactNameIsValid
  )
}

export const getUpdatedConfirmedJobs = (currentConfirmedJobs, updatedOrder, shouldExist) => {
  const currentlyExists = currentConfirmedJobs.some(order => order.id === updatedOrder.id)

  if (currentlyExists && shouldExist)
    return updateOrder(currentConfirmedJobs, updatedOrder)

  if (currentlyExists && !shouldExist)
    return removeOrder(currentConfirmedJobs, updatedOrder.id)

  if (!currentlyExists && shouldExist)
    return addOrder(currentConfirmedJobs, updatedOrder)

  return currentConfirmedJobs
}

export const updateOrder = (orders, updatedOrder) => {
  if (isNil(getOrderSequenceDate(updatedOrder)))
    return orders.map(order => order.id === updatedOrder.id
      ? updatedOrder
      : order)

  const ordersWithOrderRemoved = removeOrder(orders, updatedOrder.id)
  return addOrder(ordersWithOrderRemoved, updatedOrder)
}

export const removeOrder = (orders, orderId) => {
  return orders.filter(order => order.id !== orderId)
}

export const addOrder = (orders, order) => {
  const sequencePosition = getOrderSequenceInList(order, orders)
  const updatedOrders = [...orders]
  updatedOrders.splice(sequencePosition, 0, order)
  return updatedOrders
}

const getOrderSequenceInList = (order, ordersList) => {
  const date = getOrderSequenceDate(order)
  if (isNil(date))
    return 0

  const newIndex = ordersList.findIndex(order => getOrderSequenceDate(order) > date)
  return newIndex < 0
    ? ordersList.length
    : newIndex
}

const getOrderSequenceDate = (order) => {
  return order.targetDate || order.requiredDate
}

export const isAllConfirmedJobsDisplayed = (confirmedJobs, confirmedJobsCount) => {
  return confirmedJobs.length >= confirmedJobsCount
}

export const getFilteredOrderTypeIds = (selectedOrderTypeIds) => {
  return !isNil(selectedOrderTypeIds) && selectedOrderTypeIds.includes(UnallocatedOption.value)
    ? selectedOrderTypeIds.filter(orderTypeId => orderTypeId !== UnallocatedOption.value)
    : selectedOrderTypeIds
}

export const getConfirmedJobParams = (
  searchWithPartCode, targetStart, targetEnd, page, size, selectedOrderTypeIds
) => {
  return {
    targetStart,
    targetEnd,
    productionScheduled: false,
    jobBoardStatus: [
      JobBoardStatus.notScheduled,
      JobBoardStatus.pending,
      JobBoardStatus.wip,
      JobBoardStatus.onHold,
      JobBoardStatus.complete,
    ],
    deliveryStatus: [
      DeliveryStatus.partDelivered,
      DeliveryStatus.none,
    ],
    excludeStatus: statusesToExcludeFromProduction,
    start: page * size,
    limit: size,
    count: true,
    sortColumn: 'targetOrRequiredDate',
    searchWithPartCode: !isNil(searchWithPartCode)
      ? searchWithPartCode
      : null,
    orderTypeId: getFilteredOrderTypeIds(selectedOrderTypeIds),
    showUnallocatedOrderType: !isNil(selectedOrderTypeIds) && selectedOrderTypeIds.includes(UnallocatedOption.value),
  }
}

export const getUpdatedConfirmedJobsCount = (
  originalConfirmedJobs, originalCount, updatedOrder, newConfirmedJobs
) => {
  const allJobsWereDisplayed = isAllConfirmedJobsDisplayed(originalConfirmedJobs, originalCount)
  if (allJobsWereDisplayed)
    return newConfirmedJobs.length

  const didExist = originalConfirmedJobs.some(order => order.id === updatedOrder.id)
  const nowExists = newConfirmedJobs.some(order => order.id === updatedOrder.id)

  if (didExist && !nowExists)
    return originalCount - 1

  if (!didExist && nowExists)
    return null

  return originalCount
}

export const getOnSearchJobCardOrderItemData = (orderItems, searchText) => {
  return orderItems.filter(item => {
    return item.partCode.toLowerCase().includes(searchText.toLowerCase())
      || item.desc.toLowerCase().includes(searchText.toLowerCase())
  })
}

export const getSelectJobCardCreateTypeClassName = (createType) => {
  return createType === JobCardCreateType.LINE_ITEM
    ? 'create-type-select-sticky'
    : 'create-type-select'
}

export const getWorkCentreNameForDelivery = (delivery, workCentres) => {
  const deliveryWorkCentreId = get(delivery, 'workCentreId')
  const deliveryWorkCentre = workCentres.find(workCentre => workCentre.id === deliveryWorkCentreId)

  return get(deliveryWorkCentre, 'name')
}

export const getJobBoardStatusId = (delivery) => {
  return get(delivery, 'deliveryNote.order.jobStatus')
}

export const DefaultDeliveryOperations = {
  NONE: 'NONE',
  ONE_FOR_DELIVERY: 'ONE_FOR_DELIVERY',
  ONE_FOR_EACH_DELIVERY_ITEM: 'ONE_FOR_EACH_DELIVERY_ITEM',
}

export const defaultDeliveryOperationOptions = () => {
  return [
    {value: DefaultDeliveryOperations.NONE, label: 'No Operations'},
    {value: DefaultDeliveryOperations.ONE_FOR_DELIVERY, label: 'For The Whole Delivery'},
    {value: DefaultDeliveryOperations.ONE_FOR_EACH_DELIVERY_ITEM, label: 'For Each Delivery Item'},
  ]
}

export const isDefaultWorkCentre = (workCentre, jobCard) => {
  const jobCardDepartment = jobCard.jobCardDepartments
    .find(jobCardDepartment => jobCardDepartment.departmentId === workCentre.departmentId)

  return !isNil(jobCardDepartment) && jobCardDepartment.workCentreId === workCentre.id
}

export const isRemoveJobEnabled = (user, order) => {
  const productionScheduled = get(order, 'productionScheduled')

  return userHasPermission(user, permissions.MANAGE_JOB_CARDS)
    && isLineItemCreateType(getJobCardCreateType(order))
    && productionScheduled === false
}

export const isSelectedOnHoldReason = (jobCard, onHoldReason) => {
  const onHoldReasonId = get(jobCard, 'onHoldReasonId', null)
  return !isNil(onHoldReasonId) && onHoldReasonId === onHoldReason.id
}

export default {
  ProductionStatus,
  JobBoardStatus,
  isJobCardPending,
  isJobCardComplete,
}
